import {HttpClient, HttpHeaders, HttpResponse} from '@angular/common/http';
import {Inject, Injectable, Injector} from '@angular/core';
import {Router} from '@angular/router';
import {BehaviorSubject, EMPTY, firstValueFrom, Observable, of, throwError} from 'rxjs';
import {catchError, switchMap, tap} from 'rxjs/operators';
import {parseApiErrorArrayInHttpErrorResponse} from '../../common/api-error-array';
import {HTTP_STATUS} from '../../common/api-helpers';
import {UserRole} from '../../enums/user-role';
import {getGuestStartPageUrl, ROUTE_REDIRECTS_TOKEN, RouteRedirects} from '../../models/route-redirects';
import {AppConfigService} from '../../services/config.service';
import {LocalStorageService} from '../../services/local-storage.service';

@Injectable()
export class AuthService {

    public userRole: UserRole;

    public userRole$: Observable<UserRole>;

    public loginApiPathByRoleMap: Record<UserRole, string> = {
        [UserRole.ADMIN]:        this.appConfigService.api['backofficeLogin'],
        [UserRole.TRADER]:       this.appConfigService.api['login'],
        [UserRole.TRADER_ADMIN]: this.appConfigService.api['login']
    };

    public logoutApiPathByRoleMap: Record<UserRole, string> = {
        [UserRole.ADMIN]:        this.appConfigService.api['backofficeLogout'],
        [UserRole.TRADER]:       this.appConfigService.api['logout'],
        [UserRole.TRADER_ADMIN]: this.appConfigService.api['logout']
    };

    protected authToken: string;

    private userRoleSource = new BehaviorSubject<UserRole>(null);

    private AUTH_TOKEN_STORAGE_KEY = 'authToken';
    private USER_ROLE_STORAGE_KEY = 'userRole';

    private httpClient: HttpClient;

    private isAuthStatusInitialized: boolean = false;
    private authStatusInitProcess: Promise<void>;

    constructor(
        protected appConfigService: AppConfigService,
        private localStorageService: LocalStorageService,
        protected router: Router,
        protected injector: Injector,

        @Inject(ROUTE_REDIRECTS_TOKEN)
        protected routeRedirects: RouteRedirects
    ) {
        this.userRole$ = this.userRoleSource.asObservable();
    }

    public async initAuthStatus(): Promise<void> {
        if (this.isAuthStatusInitialized) {
            return;
        }

        if (!this.authStatusInitProcess) {
            this.authStatusInitProcess = new Promise<void>((resolve, reject) => {
                this.internalInitAuthStatus().subscribe({
                    error:    e => reject(e),
                    complete: () => {
                        this.authStatusInitProcess = undefined;
                        this.isAuthStatusInitialized = true;

                        resolve();
                    }
                });
            });
        }

        await this.authStatusInitProcess;
    }

    public redirectToLogin(): void {
        if (!this.userRole) {
            let url: string;

            if (this.appConfigService.appConfig.isGuestDashboardEnabled) {
                url = getGuestStartPageUrl(this.routeRedirects);
            } else {
                url = this.routeRedirects.LOGIN_ROUTE;
            }

            this.router.navigate([url]);
        }
    }

    public innerLogout(): void {
        this.clearCredentials();
        this.redirectToLogin();
    }

    public logout(): Promise<any> {
        const result = firstValueFrom(this.sendLogoutRequest());

        this.innerLogout();

        return result;
    }

    public getToken(): string {
        return this.authToken = this.localStorageService.get(this.AUTH_TOKEN_STORAGE_KEY);
    }

    public setCredentials(userRole: UserRole, authToken: string): void {
        this.setToken(authToken);
        this.setUserRole(userRole);
    }

    public setUserRole(userRole: UserRole): void {
        if (userRole !== this.userRole) {
            this.userRole = userRole;
            this.userRoleSource.next(userRole);
        }

        this.saveKeyValueToLocalStorage(this.USER_ROLE_STORAGE_KEY, userRole);
    }

    public authorizeByToken(token: string): Observable<any> {
        const prevLogout$ = this.getToken()
            ? this.sendLogoutRequest()
            : of(null);

        return prevLogout$.pipe(
            switchMap(() => this.authorizeByTokenForRole(UserRole.TRADER, token).pipe(
                catchError((error: HttpResponse<any>) => {

                    // If status is forbidden then try to login to backoffice endpoint
                    return (
                        error.status === HTTP_STATUS.FORBIDDEN
                            ? this.authorizeByTokenForRole(UserRole.ADMIN, token)
                            : throwError(error)
                    );
                })
            ))
        );
    }

    public authorizeWithCredentials(username: string, password: string, twoFACode?: string): Observable<any> {
        return this.authorizeWithCredentialsForRole(UserRole.TRADER, username, password, twoFACode).pipe(
            catchError((error: HttpResponse<any>) => {

                // If status is forbidden then try to login to backoffice endpoint
                return (
                    error.status === HTTP_STATUS.FORBIDDEN
                        ? this.authorizeWithCredentialsForRole(UserRole.ADMIN, username, password)
                        : throwError(error)
                );
            })
        );
    }

    /**
     * [HED-935] Returns auth token prepended with the type of the token:
     * Internal - means it's the session token issued by the app's backend.
     */
    public getAuthHeaderValue(authToken?: string): string | null | undefined {
        authToken = authToken === undefined ? this.authToken : authToken;

        return authToken ? `Internal ${authToken}` : undefined;
    }

    public getAuthHttpHeaders(headers: HttpHeaders, authToken?: string): HttpHeaders {
        const AUTH_HEADER_NAME = 'Authorization';
        let authHeaders: HttpHeaders = headers;

        if (!headers.has(AUTH_HEADER_NAME)) {
            authToken = this.getAuthHeaderValue();

            if (authToken) {
                authHeaders = headers.set(AUTH_HEADER_NAME, authToken);
            }
        }

        return authHeaders;
    }

    public encodePassword(rawPassword: string): string {
        return rawPassword;
    }

    protected internalInitAuthStatus(): Observable<any> {
        this.initCredentials();

        return EMPTY;
    }

    protected authorizeWithCredentialsForRole(
        userRole: UserRole,
        username: string,
        passwordAsUserTyped: string,
        twoFACode?: string
    ): Observable<any> {
        const password = this.encodePassword(passwordAsUserTyped);

        return this.requestAuthTokenForRole(userRole, username, password, twoFACode).pipe(
            tap(authToken => this.setCredentials(userRole, authToken))
        );
    }

    protected authorizeByTokenForRole(
        userRole: UserRole,
        token: string
    ): Observable<any> {
        return this.requestAuthByToken(token).pipe(
            tap(authToken => this.setCredentials(userRole, authToken))
        );
    }

    protected requestAuthTokenForRole(
        userRole: UserRole,
        username: string,
        passwordHash: string,
        twoFACode?: string
    ): Observable<string> {
        this.httpClient = this.httpClient || this.injector.get(HttpClient);

        const loginApiPath = this.loginApiPathByRoleMap[userRole];
        const requestBody: {
            login: string;
            password: string;
            twoFACode: string;
        } = {
            login: username.toLocaleLowerCase(),
            password: passwordHash,
            twoFACode
        };

        return this.httpClient.post(loginApiPath, requestBody, {responseType: 'text'}).pipe(
            parseApiErrorArrayInHttpErrorResponse()
        );
    }

    protected requestAuthByToken(
        token: string
    ): Observable<string> {
        this.httpClient = this.httpClient || this.injector.get(HttpClient);

        const loginApiPath =  this.appConfigService.api['loginByToken'];
        const requestBody: { token: string; } = {token};

        return this.httpClient.post(loginApiPath, requestBody, {responseType: 'text'}).pipe(
            parseApiErrorArrayInHttpErrorResponse()
        );
    }

    protected sendLogoutRequest(): Observable<any> {
        this.httpClient = this.httpClient || this.injector.get(HttpClient);

        return this.httpClient.post(this.logoutApiPathByRoleMap[this.userRole], {
            sessionToken: this.getToken()
        });
    }

    protected getUserRoleFromLocalStorage(): UserRole {
        const userRoleStr = this.localStorageService.get(this.USER_ROLE_STORAGE_KEY);

        return userRoleStr in UserRole ? userRoleStr as UserRole : UserRole.TRADER;
    }

    protected clearCredentials(): void {
        this.setToken(null);
        this.setUserRole(null);
    }

    protected initCredentials(): void {
        const authToken: string = this.getToken();
        const userRole: UserRole = this.getUserRoleFromLocalStorage();

        if (authToken && userRole) {
            try {
                this.setCredentials(userRole, authToken);
            } catch (e) {
                this.clearCredentials();
            }
        } else {
            this.clearCredentials();
        }
    }

    private setToken(authToken: string): void {
        this.authToken = authToken;

        this.saveKeyValueToLocalStorage(this.AUTH_TOKEN_STORAGE_KEY, authToken);
    }

    private saveKeyValueToLocalStorage(key: string, value: any): void {
        if (value) {
            this.localStorageService.set(key, value);
        } else {
            this.localStorageService.remove(key);
        }
    }
}
